import { useMembership } from '@/hooks/useMembership';
import type { UserRole } from '@/types/membership';

interface RoleGuardProps {
  /** Roles allowed to access this page */
  allowedRoles: UserRole[];
  children: React.ReactNode;
}

/**
 * Prevents rendering children if user's role isn't in allowedRoles.
 * Shows a "not authorized" message instead of the page content.
 * Backend still enforces authorization on API calls, but this prevents
 * technicians from seeing empty supervisor pages via direct URL navigation.
 */
export function RoleGuard({ allowedRoles, children }: RoleGuardProps) {
  const { role, isLoading } = useMembership();

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  if (!role || !allowedRoles.includes(role)) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <h2 className="text-xl font-semibold text-gray-900">Access Denied</h2>
        <p className="mt-2 text-gray-500">
          You don't have permission to view this page.
        </p>
      </div>
    );
  }

  return <>{children}</>;
}
